<template>
  <div class="sfu-provider">
    <IncomingCallDialog
      v-model="incomingOpen"
      :call="incomingCall"
      @accept="handleAccept"
      @decline="handleDecline"
    />
    <teleport to="body">
      <AvatarChatSfuPortal
        v-if="showStage"
        :mode="mode"
        :self-uid="selfUid"
        :campaign-id="campaignId"
        :target-uid="targetUid"
      />
    </teleport>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, watch } from 'vue'
  import { useUserStore } from '@/stores/userStore'
  import { useSfuCallControlStore } from '../stores/sfuCallControlStore'
  import IncomingCallDialog from './IncomingCallDialog.vue'
  import AvatarChatSfuPortal from '../../AvatarChatSfuPortal.vue'

  const userStore = useUserStore()
  const ctrl = useSfuCallControlStore()

  const selfUid = computed(() => userStore.userId || '')
  const mode = ref('campaign')
  const campaignId = ref('')
  const targetUid = ref('')
  const showStage = ref(false)
  const incomingOpen = ref(false)
  const incomingCall = computed(() => ctrl.incoming[0] || null)

  onMounted(async () => {
    if (selfUid.value) {
      try {
        await ctrl.init(selfUid.value)
      } catch {}
    }
  })

  // Ensure control socket initializes when user id becomes available later
  watch(selfUid, async (id) => {
    if (!id) return
    if (!ctrl.regs.connected) {
      try {
        await ctrl.init(id)
      } catch {}
    }
  })

  function handleAccept(call) {
    if (!call) return
    const toPeerId = String(call?.fromPeerId || '').trim()
    const conversationId = String(call?.conversationId || '').trim()
    if (toPeerId && conversationId) {
      try {
        ctrl.accept({ toPeerId, conversationId })
      } catch {}
    }
    const nextMode = String(call.mode || '').trim().toLowerCase() || 'campaign'
    if (nextMode === 'campaign') {
      const cid = String(call.conversationId || '').replace(/^campaign:/, '').trim()
      try {
        ctrl.openStageForCampaign(cid)
      } catch {}
    } else {
      const uid = String(call.fromPeerId || '').trim()
      try {
        ctrl.openStageForDirect(uid)
      } catch {}
    }
    try {
      ctrl.popIncoming(call)
    } catch {}
  }

  function handleDecline(call) {
    const toPeerId = String(call?.fromPeerId || '').trim()
    const conversationId = String(call?.conversationId || '').trim()
    if (toPeerId && conversationId) {
      try {
        ctrl.decline({ toPeerId, conversationId })
      } catch {}
    }
    try {
      ctrl.popIncoming(call)
    } catch {}
    incomingOpen.value = false
  }

  // react to external open/close requests from store
  watch(
    () => ctrl.current,
    (c) => {
      if (!c) return
      if (c.showStage) {
        mode.value = c.mode || 'campaign'
        campaignId.value = c.campaignId || ''
        targetUid.value = c.targetUid || ''
        showStage.value = true
      } else {
        showStage.value = false
      }
    },
    { deep: true, immediate: true }
  )

  // Auto-open/close incoming dialog based on control store
  watch(
    () => incomingCall.value,
    (c) => {
      incomingOpen.value = !!c
    },
    { immediate: true }
  )
</script>

<style scoped>
  .sfu-provider {
    display: contents;
  }
</style>
